import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';
import { parseIntOrDefault } from '@theme/utilities';

/**
 * A custom element that allows the user to select a quantity.
 *
 * This component follows a pure event-driven architecture where quantity changes
 * are broadcast via QuantitySelectorUpdateEvent. Parent components that contain
 * quantity selectors listen for these events and handle them according to their
 * specific needs, with event filtering ensuring each parent only processes events
 * from its own quantity selectors to prevent conflicts between different cart
 * update strategies.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 * @property {HTMLButtonElement} minusButton
 * @property {HTMLButtonElement} plusButton
 *
 * @extends {Component<Refs>}
 */
export class QuantitySelectorComponent extends Component {
  requiredRefs = ['quantityInput', 'minusButton', 'plusButton'];
  serverDisabledMinus = false;
  serverDisabledPlus = false;
  initialized = false;

  connectedCallback() {
    super.connectedCallback();

    // Capture server-disabled state on first load
    // Only mark as server-disabled if button is disabled for a server-side reason
    // (e.g., can_update_quantity == false), not just because value = min
    const { minusButton, plusButton, quantityInput } = this.refs;

    // Children not parsed yet (slow device): skip init instead of crashing. Refs are re-synced by Component.
    if (!minusButton || !plusButton || !quantityInput) return;

    if (minusButton && quantityInput) {
      const currentValue = parseInt(quantityInput.value) || 0;
      const minValue = parseInt(quantityInput.min) || 1;
      // Treat as server-disabled if:
      // 1. Button is disabled AND value > min (unusual case, likely server-side)
      // 2. Button is disabled AND input is also disabled (can_update_quantity == false)
      if (minusButton.disabled && (currentValue > minValue || quantityInput.disabled)) {
        this.serverDisabledMinus = true;
      }
    }

    if (plusButton && quantityInput) {
      const currentValue = parseInt(quantityInput.value) || 0;
      const maxValue = quantityInput.max ? parseInt(quantityInput.max) : null;
      // Treat as server-disabled if:
      // 1. Button is disabled AND value < max (unusual case, likely server-side)
      // 2. Button is disabled AND input is also disabled (can_update_quantity == false)
      if (plusButton.disabled && ((maxValue === null || currentValue < maxValue) || quantityInput.disabled)) {
        this.serverDisabledPlus = true;
      }
    }

    quantityInput.readOnly = true;

    this.initialized = true;
    this.updateButtonStates();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  /**
   * Updates cart quantity and refreshes component state
   * @param {number} cartQty - The quantity currently in cart for this variant
   */
  setCartQuantity(cartQty) {
    this.refs.quantityInput.setAttribute('data-cart-quantity', cartQty.toString());
    this.updateCartQuantity();
  }

  /**
   * Checks if the current quantity can be added to cart without exceeding max
   * @returns {{canAdd: boolean, maxQuantity: number|null, cartQuantity: number, quantityToAdd: number}} Validation result
   */
  canAddToCart() {
    const { max, cartQuantity, value } = this.getCurrentValues();
    const quantityToAdd = value;
    const wouldExceedMax = max !== null && cartQuantity + quantityToAdd > max;

    return {
      canAdd: !wouldExceedMax,
      maxQuantity: max,
      cartQuantity,
      quantityToAdd,
    };
  }

  /**
   * Gets the current quantity value
   * @returns {string} The current value
   */
  getValue() {
    return this.refs.quantityInput.value;
  }

  /**
   * Sets the current quantity value
   * @param {string} value - The value to set
   */
  setValue(value) {
    this.refs.quantityInput.value = value;
  }

  /**
   * Updates min/max/step constraints and snaps value to valid increment
   * @param {string} min - Minimum value
   * @param {string|null} max - Maximum value (null if no max)
   * @param {string} step - Step increment
   */
  updateConstraints(min, max, step) {
    const { quantityInput } = this.refs;
    const currentValue = parseInt(quantityInput.value) || 0;

    quantityInput.min = min;
    if (max) {
      quantityInput.max = max;
    } else {
      quantityInput.removeAttribute('max');
    }
    quantityInput.step = step;

    const newMin = parseIntOrDefault(min, 1);
    const newStep = parseIntOrDefault(step, 1);
    const effectiveMax = this.getEffectiveMax();

    // Snap to valid increment if not already aligned
    let newValue = currentValue;
    if ((currentValue - newMin) % newStep !== 0) {
      // Snap DOWN to closest valid increment
      newValue = newMin + Math.floor((currentValue - newMin) / newStep) * newStep;
    }

    // Ensure value is within bounds
    newValue = Math.max(newMin, Math.min(effectiveMax ?? Infinity, newValue));

    if (newValue !== currentValue) {
      quantityInput.value = newValue.toString();
    }

    this.updateButtonStates();
  }

  /**
   * Gets current values from DOM (fresh read every time)
   * @returns {{min: number, max: number|null, step: number, value: number, cartQuantity: number}}
   */
  getCurrentValues() {
    // Fall back to a DOM lookup if refs were not ready (slow devices), so Add to Cart never crashes here.
    const quantityInput = this.refs.quantityInput ?? this.querySelector('[ref="quantityInput"]');

    if (!(quantityInput instanceof HTMLInputElement)) {
      return { min: 1, max: null, step: 1, value: 1, cartQuantity: 0 };
    }

    return {
      min: parseIntOrDefault(quantityInput.min, 1),
      max: parseIntOrDefault(quantityInput.max, null),
      step: parseIntOrDefault(quantityInput.step, 1),
      value: parseIntOrDefault(quantityInput.value, 0),
      cartQuantity: parseIntOrDefault(quantityInput.getAttribute('data-cart-quantity'), 0),
    };
  }

  /**
   * Gets the effective maximum value for this quantity selector
   * Product page: max - cartQuantity (how many can be added)
   * Also considers inventory_quantity when inventory is managed
   * Override in subclass for different behavior
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const quantityInput = this.refs.quantityInput ?? this.querySelector('[ref="quantityInput"]');
    if (!quantityInput) return null;
    const { max, cartQuantity, min } = this.getCurrentValues();

    // Check if inventory is managed
    const inventoryQuantity = quantityInput.getAttribute('data-inventory-quantity');
    const inventoryPolicy = quantityInput.getAttribute('data-inventory-policy');
    
    let effectiveMax = max;
    
    // If inventory is managed with deny policy, use inventory_quantity as the limit
    if (inventoryQuantity !== null && inventoryPolicy === 'deny') {
      const inventoryMax = parseInt(inventoryQuantity, 10);
      if (!isNaN(inventoryMax)) {
        // Use the minimum of max (if exists) and inventory_quantity
        if (effectiveMax === null) {
          effectiveMax = inventoryMax;
        } else {
          effectiveMax = Math.min(effectiveMax, inventoryMax);
        }
      }
    }
    
    if (effectiveMax === null) return null;
    // Product page: can only add what's left (considering both max and inventory)
    return Math.max(effectiveMax - cartQuantity, min);
  }

  /**
   * Updates button states based on current value and limits
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    if (!minusButton || !plusButton) return;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Only manage buttons that weren't server-disabled
    if (!this.serverDisabledMinus) {
      minusButton.disabled = value <= min;
    }

    if (!this.serverDisabledPlus) {
      plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
    }
  }

  /**
   * Updates quantity by a given step
   * @param {number} stepMultiplier - Positive for increase, negative for decrease
   */
  updateQuantity(stepMultiplier) {
    const { quantityInput } = this.refs;
    const { min, step, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    const newValue = Math.min(effectiveMax ?? Infinity, Math.max(min, value + step * stepMultiplier));
    
    if (quantityInput.closest(".product-information") || quantityInput.closest(".sticky-add-to-cart")) {
      const allQuantitySelectors = document.querySelectorAll('product-form-component quantity-selector-component, sticky-add-to-cart quantity-selector-component');
      for (const quantitySelector of allQuantitySelectors) {
        const inputBox = quantitySelector.querySelector('input[type="number"][name="quantity"]');
        if (inputBox) {
          inputBox.value = newValue.toString();
          quantitySelector.onQuantityChange();
          quantitySelector.updateButtonStates();
        }
      }
    }else{
      quantityInput.value = newValue.toString();
      this.onQuantityChange();
      this.updateButtonStates();
    }
  }

  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();
    this.updateQuantity(1);
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();
    this.updateQuantity(-1);
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event (on blur).
   * Validates and snaps to valid values.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    event.preventDefault();
    const { quantityInput } = this.refs;
    const { min, step } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Snap to bounds
    const quantity = Math.min(effectiveMax ?? Infinity, Math.max(min, parseInt(event.target.value) || 0));

    // Validate step increment
    if ((quantity - min) % step !== 0) {
      // Set the invalid value and trigger native HTML validation
      quantityInput.value = quantity.toString();
      quantityInput.reportValidity();
      return;
    }

    quantityInput.value = quantity.toString();
    this.onQuantityChange();
    this.updateButtonStates();
  }

  /**
   * Handles the quantity change event.
   */
  onQuantityChange() {
    const { quantityInput } = this.refs;
    const newValue = parseInt(quantityInput.value);

    this.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine) || undefined));
  }

  /**
   * Updates the cart quantity from data attribute and refreshes button states
   * Called when cart is updated from external sources
   */
  updateCartQuantity() {
    const { quantityInput } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Clamp value to new effective max if necessary
    const clampedValue = Math.min(effectiveMax ?? Infinity, Math.max(min, value));
    
    if (clampedValue !== value) {
      quantityInput.value = clampedValue.toString();
    }

    this.updateButtonStates();
  }

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
